import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ReportsService } from '@app/shared/services/reports.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { DEFAULT_LOCALE, ReportCardGroup, ReportCardNames } from '@app/shared/constants';
import { formatNumber } from '@angular/common';
import { LocalStorageService } from 'ngx-webstorage';
import { HelpersService } from '@app/shared/services/helpers.service';

export interface ReportCard {
  type: ReportCardGroup;
  title: ReportCardNames,
  iconClass: string,
  titleClass: string,
  isInitialized: boolean,
  isLoading: boolean,
  loadingFailed: boolean,
  total: string,
  isCurrency?: boolean,
  subStatTemplateRef?: string
  subStatTemplate?: TemplateRef<any>,
  subStatsContext?: any,
}

export interface ReportCardGroups {
  id: string,
  label: string,
  showGroup: boolean,
  cards: ReportCard[],
}

@Component({
  standalone: false,
  selector: 'app-machool-report-cards',
  templateUrl: './machool-report-cards.component.html',
})
export class MachoolReportCardsComponent implements OnInit {
  @Input() filterForm: FormGroup | undefined;
  @Input() range: FormGroup | undefined;
  @Input() canDownloadCardData: boolean = false;

  @ViewChild('accountSubStats') accountSubStats!: TemplateRef<any>;
  @ViewChild('revenueSubStats') revenueSubStats!: TemplateRef<any>;
  @ViewChild('greenSubStats') greenSubStats!: TemplateRef<any>;
  @ViewChild('reconSubStats') reconSubStats!: TemplateRef<any>;
  @ViewChild('maOnlyRevenueSubStats') maOnlyRevenueSubStats!: TemplateRef<any>;
  @ViewChild('walletSubStats') walletSubStats!: TemplateRef<any>;
  @ViewChild('numShipmentsSubStats') numShipmentsSubStats!: TemplateRef<any>;
  @ViewChild('shipmentsSubStats') shipmentsSubStats!: TemplateRef<any>;
  @ViewChild('loyaltyRewardsSubStats') loyaltyRewardsSubStats!: TemplateRef<any>;
  @ViewChild('commissionSubStats') commissionSubStats!: TemplateRef<any>;
  @ViewChild('signeLocalSubStats') signeLocalSubStats!: TemplateRef<any>;

  public loading: boolean = true;
  public downloadingReportData: boolean = false;

  public allCurrentCards: ReportCardGroups[] = [
    {
      id: 'accountCards',
      label: 'Accounts',
      showGroup: true,
      cards: [
        {
          type: ReportCardGroup.ACCOUNTS,
          title: ReportCardNames.ACCOUNTS,
          iconClass: 'icon-user bg-report-red',
          titleClass: 'text-report-red',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
          subStatTemplateRef: 'accountSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.NEW_ACCOUNTS,
          title: ReportCardNames.NEW_ACCOUNTS,
          iconClass: 'icon-users bg-report-red',
          titleClass: 'text-report-red',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
        },
        {
          type: ReportCardGroup.STARTED_SHIPPING,
          title: ReportCardNames.STARTED_SHIPPING,
          iconClass: 'icon-users bg-report-red',
          titleClass: 'text-report-red',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
        },
      ]
    },
    {
      id: 'revenueCards',
      label: 'Revenue',
      showGroup: true,
      cards: [
        {
          type: ReportCardGroup.REVENUE,
          title: ReportCardNames.REVENUE,
          titleClass: 'text-report-indigo',
          iconClass: 'icon-history bg-report-indigo',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'revenueSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.REVENUE_BY_CHARGE,
          title: ReportCardNames.REVENUE_BY_CHARGE,
          titleClass: 'text-report-indigo',
          iconClass: 'icon-history bg-report-indigo',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'revenueSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.GREEN_SHIPMENTS_REVENUE,
          title: ReportCardNames.GREEN_SHIPMENTS_REVENUE,
          titleClass: 'text-report-green',
          iconClass: 'icon-leaf bg-report-green',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'greenSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.STRIPE,
          title: ReportCardNames.STRIPE,
          titleClass: 'text-report-stripe',
          iconClass: 'icon-creditcard bg-report-stripe',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
        },
        {
          type: ReportCardGroup.RECON_ADJUSTMENTS,
          title: ReportCardNames.RECON_ADJUSTMENTS,
          titleClass: 'text-report-recon',
          iconClass: 'icon-charge bg-report-recon',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'reconSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.CQCD,
          title: ReportCardNames.CQCD,
          titleClass: 'text-tier-cqcd',
          iconClass: 'icon-cqcd bg-tier-cqcd',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'commissionSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.SHIP_ALBERTA,
          title: ReportCardNames.SHIP_ALBERTA,
          titleClass: 'text-ship-alberta',
          iconClass: 'icon-ship-alberta bg-tier-ship-alberta',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'commissionSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.RCC,
          title: ReportCardNames.RCC,
          titleClass: 'text-rcc',
          iconClass: 'icon-rcc bg-tier-rcc',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'commissionSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.SIGNE_LOCAL,
          title: ReportCardNames.SIGNE_LOCAL,
          titleClass: 'text-signe-local',
          iconClass: 'icon-signe-local bg-signe-local',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'signeLocalSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.PANIER_DA_CHAT,
          title: ReportCardNames.PANIER_DA_CHAT,
          titleClass: 'text-tier-panier',
          iconClass: 'icon-panierdachat bg-tier-panier',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'revenueSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.WALLET_BALANCE,
          title: ReportCardNames.WALLET_BALANCE,
          titleClass: 'text-report-wallet-balance',
          iconClass: 'icon-logo bg-report-wallet-balance',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'walletSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.REVENUE_BY_BYOA,
          title: ReportCardNames.REVENUE_BY_BYOA,
          titleClass: 'text-report-indigo',
          iconClass: 'icon-history bg-report-indigo',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'numShipmentsSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.LOYALTY_REWARDS,
          title: ReportCardNames.LOYALTY_REWARDS,
          titleClass: 'text-report-wallet-balance',
          iconClass: 'icon-box-collect bg-report-wallet-balance',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: true,
          subStatTemplateRef: 'loyaltyRewardsSubStats',
          subStatsContext: undefined,
        },
      ]
    },
    {
      id: 'shipmentCards',
      label: 'Shipments',
      showGroup: true,
      cards: [
        {
          type: ReportCardGroup.SHIPMENTS,
          title: ReportCardNames.SHIPMENTS,
          titleClass: 'text-report-aqua',
          iconClass: 'icon-box bg-report-aqua',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
          subStatTemplateRef: 'shipmentsSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.SHIPMENTS_MANUAL,
          title: ReportCardNames.SHIPMENTS_MANUAL,
          titleClass: 'text-report-aqua',
          iconClass: 'icon-box bg-report-aqua',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
          subStatTemplateRef: 'shipmentsSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.SHIPMENTS_ECOMMERCE,
          title: ReportCardNames.SHIPMENTS_ECOMMERCE,
          titleClass: 'text-report-aqua',
          iconClass: 'icon-box bg-report-aqua',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
          subStatTemplateRef: 'shipmentsSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.SHIPMENTS_API,
          title: ReportCardNames.SHIPMENTS_API,
          titleClass: 'text-report-aqua',
          iconClass: 'icon-box bg-report-aqua',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
          subStatTemplateRef: 'shipmentsSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.SHIPMENTS_CONNECT,
          title: ReportCardNames.SHIPMENTS_CONNECT,
          titleClass: 'text-report-aqua',
          iconClass: 'icon-box bg-report-aqua',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
          subStatTemplateRef: 'shipmentsSubStats',
          subStatsContext: undefined,
        },
        {
          type: ReportCardGroup.SHIPMENTS_FREIGHT,
          title: ReportCardNames.SHIPMENTS_FREIGHT,
          titleClass: 'text-report-aqua',
          iconClass: 'icon-box bg-report-aqua',
          isInitialized: false,
          isLoading: false,
          loadingFailed: false,
          total: '',
          isCurrency: false,
          subStatTemplateRef: 'shipmentsSubStats',
          subStatsContext: undefined,
        },
      ]
    }
  ];

  // Flatten all cards for easier management
  public defaultSelectedCards: ReportCardGroup[] =
    this.allCurrentCards
      .flatMap(cardGroup => cardGroup.cards)
      .map((card) => card.type);
  public currentSelectedCards: ReportCardGroup[] = this.defaultSelectedCards;

  constructor(
    private logger: LoggerService,
    private reportsService: ReportsService,
    private bannerNotificationsService: BannerNotificationsService,
    private localStorageService: LocalStorageService,
    private helpersService: HelpersService,
  ) {}

  ngOnInit() {
    this.loading = true;
    this.loadPreferencesFromStorage();
    this.loading = false;
  }

  protected readonly ReportCardGroupTypes = ReportCardGroup;

  // fetch all the data for the cards
  public getAllInformation(): void {
    if (!this.isAnyCardLoading()) {
      const currentCards = this.getCurrentSelectedCards();
      currentCards.forEach((card) => {
        this.refreshReportCard(card);
      });
    }
  }

  public isAnyCardLoading(): boolean {
    const currentCards = this.getCurrentSelectedCards();
    return currentCards.some((card) => card.isLoading);
  }

  public onPreferencesSaved(selectedCards: ReportCardGroup[]) {
    this.currentSelectedCards = selectedCards;
    this.localStorageService.store('reportCardPreferences', JSON.stringify(selectedCards));
    this.updateCardGroups();
  }

  // DOWNLOAD CARD DATA
  public downloadCardData(cardType: ReportCardGroup): void {
    if (this.canDownloadCardData) {
      this.downloadingReportData = true;
      const query: any = this.reportsService.getFilterQuery(this.range, this.filterForm);
      query.report_card_group = cardType;
      this.reportsService.getSummaryCardData(query).subscribe(
        (res) => {
          this.bannerNotificationsService.success("Report is generating, Slack will be notified once it's ready.");
          this.downloadingReportData = false;
        },
        (err: Error) => {
          this.downloadingReportData = false;
          this.logger.error('GET accounts report data error', err);
          this.bannerNotificationsService.error(err.message);
        }
      );
    }
  }

  public getSubStatsTemplate(templateName?: string): TemplateRef<any> | undefined {
    if (!templateName) return undefined;

    switch(templateName) {
      case 'accountSubStats': return this.accountSubStats;
      case 'revenueSubStats': return this.revenueSubStats;
      case 'maOnlyRevenueSubStats': return this.maOnlyRevenueSubStats;
      case 'greenSubStats': return this.greenSubStats;
      case 'reconSubStats': return this.reconSubStats;
      case 'walletSubStats': return this.walletSubStats;
      case 'numShipmentsSubStats': return this.numShipmentsSubStats;
      case 'shipmentsSubStats': return this.shipmentsSubStats;
      case 'loyaltyRewardsSubStats': return this.loyaltyRewardsSubStats;
      case 'commissionSubStats': return this.commissionSubStats;
      case 'signeLocalSubStats': return this.signeLocalSubStats;
      default: return undefined;
    }
  }

  public refreshReportCard(cardToUpdate: ReportCard): void {
    if (this.currentSelectedCards.includes(cardToUpdate.type)) {
      cardToUpdate.isLoading = true;
      cardToUpdate.isInitialized = true;
      cardToUpdate.loadingFailed = false;
      let query: any = this.reportsService.getFilterQuery(this.range, this.filterForm);
      query.report_card_group = cardToUpdate.type;
      this.reportsService.getSummaryCard(query).subscribe(
        (res) => {
          let totalReturned = 0;

          // TODO: Refactor the returned data to be more consistent [last validated 2026-10-16]
          // Setup the report content needed to render the cards.  In some cases, we also set subStatsContext
          // for the subStateTemplate to use.
          if ('totalShipments' in res){
            totalReturned = res.totalShipments;
            cardToUpdate.subStatsContext = {
              maShipments: formatNumber(res.maShipments, DEFAULT_LOCALE),
              byoaShipments: formatNumber(res.byoaShipments, DEFAULT_LOCALE),
            };
          } else if ('eligibleAmount' in res) {
            totalReturned = res.eligibleAmount;
            cardToUpdate.subStatsContext = {
              commissionAmount: res.commissionAmount || 0
            };
          } else if ('customerRevenue' in res) {
            totalReturned = res.slPayment || 0;
            cardToUpdate.subStatsContext = {
              machoolFee: res.machoolFee || 0
            };
          } else if ('totalRevenue' in res) {
            totalReturned = res.totalRevenue;
            cardToUpdate.subStatsContext = {
              maRevenue: res.maRevenue || 0,
              byoaRevenue: res.byoaRevenue || 0,
            };
          } else if ('greenRevenue' in res) {
            totalReturned = res.greenRevenue;
            cardToUpdate.subStatsContext = {
              greenShipments: formatNumber(res.greenShipments, DEFAULT_LOCALE)
            }
          } else if ('totalAccounts' in res){
            totalReturned = res.totalAccounts;
            cardToUpdate.subStatsContext = {
              accountsWithOne: res.accountsWithOne ? formatNumber(res.accountsWithOne, DEFAULT_LOCALE) : 0
            };
          } else if ('stripeRevenue' in res){
            totalReturned = res.stripeRevenue;
          } else if ('byoaRevenue' in res){
            totalReturned = res.byoaRevenue;
            cardToUpdate.subStatsContext = { totalByoaShipments: formatNumber(res.byoaShipments, DEFAULT_LOCALE) };
          } else if ('reconAdjustment' in res){
            totalReturned = res.reconAdjustment;
            cardToUpdate.subStatsContext = {
              stripeReconAdjustment: res.stripeReconAdjustment || 0,
              maReconAdjustment: res.maReconAdjustment || 0,
            };
          } else if ('walletBalance' in res){
            totalReturned = res.walletBalance || 0;
            cardToUpdate.subStatsContext = { totalWalletAccounts: formatNumber(res.walletAccounts || 0, DEFAULT_LOCALE) };
          } else if ('totalProcessedRewards' in res){
            totalReturned = res.totalProcessedRewards;
            cardToUpdate.subStatsContext = {
              totalPendingRewards: res.totalPendingRewards,
              totalAvailableRewards: res.totalAvailableRewards,
            };
          }

          // The report card component expected the total to be returned as a string since we can pre-format it
          // if it's currency, etc.
          cardToUpdate.total = `${totalReturned}`;
          if (!cardToUpdate.isCurrency) {
            cardToUpdate.total = formatNumber(totalReturned, DEFAULT_LOCALE);
          }

          // If there is a substat template, set up the template properly based on the string reference.
          // This is done here to ensure that the templates are properly set up in the UI before the
          // loaded card UI is rendered.
          if (cardToUpdate.subStatTemplateRef) {
            cardToUpdate.subStatTemplate = this.getSubStatsTemplate(cardToUpdate.subStatTemplateRef);
          }

          cardToUpdate.isLoading = false;
        },
        (err: Error) => {
          cardToUpdate.isLoading = false;
          cardToUpdate.loadingFailed = true;
          this.logger.error(`GET ${cardToUpdate.type} report error`, err);
        }
      )

    }
  }

  private loadPreferencesFromStorage(): void {
    const stored = this.localStorageService.retrieve('reportCardPreferences');
    this.currentSelectedCards = stored ? JSON.parse(stored) : this.defaultSelectedCards;
    this.updateCardGroups();
  }

  private updateCardGroups(): void {
    this.allCurrentCards.forEach((cardGroup) => {
      cardGroup.showGroup = this.helpersService.hasAnyCommonElement(cardGroup.cards?.map(card => card.type), this.currentSelectedCards);
    });
  }

  private getCurrentSelectedCards(): ReportCard[] {
    const allCards = this.allCurrentCards.flatMap(cardGroup => cardGroup.cards);
    return allCards.filter((card) => this.currentSelectedCards.includes(card.type));
  }
}
